import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, EventConstants } from "utils/constants";
import { InvoiceDetailsViewProps } from "models/propTypes/invoicePropTypes";
//#endregion
//#region components
import {
  ViewPanel,
  FormValidity,
  PageLayouts,
  EntityTitle,
  GenericCommentsView,
  Skeletons,
  Profiles,
} from "components/common";
import InvoiceFooter from "./footer";
import InvoiceOverdueBanner from "./banners/overdueBanner";
import ReminderFeeNotPaidBanner from "components/invoice/banners/reminderFeeNotPaidBanner";
import InvoiceDetailsFormContainer from "./forms/detailsFormContainer";
import InvoiceVouchers from "./transactions";
import InvoiceSimilarList from "./similarList";
import InvoiceStateFlow from "./stateFlow";
import FromRecurringInvoiceBanner from "./banners/fromRecurringBanner";
import CreditedInvoiceBanner from "./banners/creditedInvoiceBanner";
import CreditInvoiceBanner from "./banners/creditInvoiceBanner";
import AutomaticReminderBanner from "./banners/automaticReminderBanner";
import InvoiceDebtCollectionBanner from "./banners/invoiceDebtCollectionBanner";
import InvoiceVersions from "./versions";
import useInvoiceFileViewer from "./useFileViewer";
//#endregion
import { LocalizationHelperInstance, EventHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoiceDetailsView: React.FC<InvoiceDetailsViewProps> = (props) => {
  const { invoice, reloadInvoice } = props;
  const formRef = React.useRef<FormHandles>(null);

  const onUpdateInvoice = () => {
    return (formRef.current && formRef.current.submit()) || new Promise<void>((resolve) => resolve());
  };

  const { fileViewerComponent, attachments } = useInvoiceFileViewer(invoice);

  const [displayType, setDisplayType] = React.useState<StyleEnums.DisplayType>(StyleEnums.DisplayType.Info);

  React.useEffect(() => {
    const updatedDisplayType = getUIDisplayState();
    if (updatedDisplayType !== displayType) setDisplayType(updatedDisplayType);
  }, [invoice?.status]);

  const getUIDisplayState = () => {
    switch (invoice?.status) {
      case WintSuperkollAPIModel.InvoiceStatus.Paid:
        return StyleEnums.DisplayType.Success;
      case WintSuperkollAPIModel.InvoiceStatus.OverdueReminderSent:
      case WintSuperkollAPIModel.InvoiceStatus.OverdueReminderNotSent:
      case WintSuperkollAPIModel.InvoiceStatus.Collection:
        return StyleEnums.DisplayType.Danger;
      case WintSuperkollAPIModel.InvoiceStatus.Cancelled:
        return StyleEnums.DisplayType.None;
      default:
        return StyleEnums.DisplayType.Info;
    }
  };

  const onSubTitleClick = () => {
    EventHelper.publishEvent(EventConstants.CloseDrawer);
  };

  return invoice ? (
    <FormValidity
      render={(isValid, onValidityChange) => (
        <PageLayouts.EntityViewLayout
          displayType={displayType}
          upperLeftComponent={
            <EntityTitle
              status={
                invoice.status === WintSuperkollAPIModel.InvoiceStatus.Cancelled
                  ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Cancel)
                  : invoice.paymentState === WintSuperkollAPIModel.InvoicePaymentState.Paid
                  ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Paid)
                  : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Unpaid)
              }
              subTitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceTo, "", {
                id: `${invoice.serialNumber}`,
              })}
              title={
                <Profiles.CustomerProfileLink
                  id={invoice.customerId}
                  name={invoice.customerName ?? ""}
                  onProfileClick={onSubTitleClick}
                  className={cssExports.titleLink}
                />
              }
            />
          }
          upperRightComponent={
            invoice ? (
              <InvoiceStateFlow status={invoice.paymentState} invoiceId={invoice.id} />
            ) : (
              <Skeletons.StateFlowSkeleton key="steps-skeleton" stepCount={4} />
            )
          }
          lowerLeftComponent={fileViewerComponent}
          footer={
            <InvoiceFooter
              isValid={isValid}
              invoice={invoice}
              createOrUpdateInvoice={onUpdateInvoice}
              reloadHandler={reloadInvoice}
              markAsDoneHandler={reloadInvoice}
              hideSave={false}
            />
          }
        >
          <>
            {(invoice.status === WintSuperkollAPIModel.InvoiceStatus.OverdueReminderNotSent ||
              invoice.status === WintSuperkollAPIModel.InvoiceStatus.OverdueReminderSent) && (
              <InvoiceOverdueBanner invoice={invoice} reloadHandler={reloadInvoice} />
            )}

            {invoice.status === WintSuperkollAPIModel.InvoiceStatus.ReminderFeeNotFullyPaid && (
              <ReminderFeeNotPaidBanner invoice={invoice} reloadHandler={reloadInvoice} />
            )}

            {invoice.status === WintSuperkollAPIModel.InvoiceStatus.Collection && (
              <InvoiceDebtCollectionBanner invoice={invoice} />
            )}

            {invoice.fromRecurring && <FromRecurringInvoiceBanner invoice={invoice} displayType={displayType} />}

            {invoice.creditStatus === WintSuperkollAPIModel.InvoiceCreditStatus.Credited && (
              <CreditedInvoiceBanner invoice={invoice} displayType={displayType} />
            )}

            {invoice.creditStatus === WintSuperkollAPIModel.InvoiceCreditStatus.Credit && (
              <CreditInvoiceBanner invoice={invoice} displayType={displayType} />
            )}

            {invoice.isAutomaticRemindersEnabled && !invoice.credit && (
              <AutomaticReminderBanner invoiceId={invoice.id} reloadHandler={reloadInvoice} />
            )}

            {invoice.pDFCount > 1 && <InvoiceVersions invoice={invoice} />}

            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
              icon={IconDefinitionConstants.WEdit}
              classes={{ content: cssExports.detailsFormContainer }}
            >
              <InvoiceDetailsFormContainer
                ref={formRef}
                invoice={invoice}
                onFormValidationChange={onValidityChange}
                loadInvoice={reloadInvoice}
                attachments={attachments ?? []}
                fileViewer={fileViewerComponent}
              />
            </ViewPanel>
            <InvoiceVouchers id={invoice.id} />
            <GenericCommentsView parentId={invoice.id} type={WintSuperkollAPIModel.GenericCommentType.Invoice} />
            <InvoiceSimilarList id={invoice.id} />
          </>
        </PageLayouts.EntityViewLayout>
      )}
    />
  ) : (
    <></>
  );
};
export default InvoiceDetailsView;
